/**
 * The Strat screen (docs/plans/strategy-lists.md, phase 5, owner approved
 * 2026-10-25): which screen is a strategy, its default columns, the stepper's
 * months and the status badge.
 *
 * A strategy is a group whose code starts `STRAT_` (qv-data `strategies/serve.py`),
 * shown only to `ind:PD`. The columns its rows carry are the server's
 * `strat*` fields; this module only decides which are on screen.
 */
import type { ScreenerColumn } from "@qufolio/model";
import type { ScreenerFrame } from "@qufolio/transport";

export const STRAT_PREFIX = "STRAT_";

/** The strategy code when exactly one Strat chip is the screen, else null. */
export function strategyCode(codes: readonly string[]): string | null {
  return codes.length === 1 && codes[0]!.toUpperCase().startsWith(STRAT_PREFIX) ? codes[0]!.toUpperCase() : null;
}

const pct = (field: string, header: string, signed = false): ScreenerColumn => ({
  field,
  header,
  precision: 1,
  width: 84,
  ...(signed
    ? {
        format: [
          { when: { op: "gt", value: 0 }, style: { fg: "positive" } },
          { when: { op: "lt", value: 0 }, style: { fg: "negative" } },
        ],
      }
    : {}),
});

/** The owner's default set (phase 5 #1), in order. */
const DEFAULTS: readonly ScreenerColumn[] = [
  { field: "symbol", header: "Symbol", pinned: "left", width: 80 },
  { field: "name", header: "Name", width: 200 },
  { field: "stratRole", header: "Role", width: 130 },
  pct("stratWeight", "Weight %"),
  pct("stratPrevWeight", "Prev %"),
  pct("stratChange", "Change", true),
  { field: "stratAction", header: "Action", width: 72 },
  // Text from `withEntryText`: "May 2026", or "Jul–Oct 2026 · sold" (owner, 2026-10-27).
  { field: "stratSinceText", header: "Since", type: "text", width: 130 },
  // Text, from `withEntryText`: the close, or "stand-in" where the run began
  // before the fund existed (phase 5 #5). A number column has no way to say so.
  { field: "stratEntry", header: "Entry", type: "text", width: 84 },
  pct("stratSincePct", "Since %", true),
  { field: "close", header: "Last", precision: 2, width: 84 },
  {
    field: "changePercent",
    header: "Chg %",
    precision: 2,
    width: 80,
    format: [
      { when: { op: "gte", value: 0 }, style: { fg: "positive" } },
      { when: { op: "lt", value: 0 }, style: { fg: "negative" } },
    ],
  },
];

export const STRAT_COLUMN_FIELDS: readonly string[] = DEFAULTS.map((c) => c.field);

/**
 * The columns a Strat screen shows. A screen that carries any `strat*` column
 * has been set up for strategies, so it is **exactly the user's columns** —
 * forcing the defaults back made a removed Name reappear (owner, 2026-10-27).
 * Any other screen gets the defaults. A saved Entry (`stratEntryClose`) is
 * drawn as the text `stratEntry`, so it can say "stand-in". The saved
 * `config.columns` is never rewritten — this is the grid's copy only.
 */
export function strategyColumns(saved: readonly ScreenerColumn[] | undefined): ScreenerColumn[] {
  const columns = saved ?? [];
  if (!columns.some((c) => c.field.startsWith("strat"))) {
    // The defaults, then the screen's own other columns: the docked editor
    // shows this list and its first edit saves it, so it must not drop what
    // the screen shows on every other universe (owner, 2026-10-27).
    return [...DEFAULTS, ...columns.filter((c) => !STRAT_COLUMN_FIELDS.includes(c.field))];
  }
  return columns.map((c) =>
    c.field === "stratEntryClose"
      ? { ...c, field: "stratEntry", type: "text" }
      : c.field === "stratSince"
        ? { ...c, field: "stratSinceText", type: "text" }
        : c,
  );
}

/**
 * A non-Strat universe's copy of the columns, without the `strat*` ones —
 * blank there, and saved on the screen for when a Strat list is on again.
 */
export function withoutStratColumns(columns: readonly ScreenerColumn[]): readonly ScreenerColumn[] {
  return columns.some((c) => c.field.startsWith("strat")) ? columns.filter((c) => !c.field.startsWith("strat")) : columns;
}

/*
 * Which strategy the screener is showing, for the docked editor — a sibling in
 * the layout, so it cannot be a prop. Module scope with subscribers, for
 * `symbol-index.ts`'s reason; `App` reads it with `useSyncExternalStore`.
 */
let onScreen: string | null = null;
const listeners = new Set<() => void>();

export function strategyOnScreen(): string | null {
  return onScreen;
}

export function publishStrategyOnScreen(code: string | null): void {
  if (code === onScreen) return;
  onScreen = code;
  for (const listener of listeners) listener();
}

export function subscribeStrategyOnScreen(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** `YYYY-MM` (or a `YYYY-MM-DD` the server sent) moved by `by` months. */
export function shiftMonth(month: string, by: number): string {
  const [y, m] = month.slice(0, 7).split("-").map(Number) as [number, number];
  const index = y * 12 + (m - 1) + by;
  const year = Math.floor(index / 12);
  return `${year}-${String((index % 12) + 1).padStart(2, "0")}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function monthLabel(month: string): string {
  const [y, m] = month.slice(0, 7).split("-").map(Number) as [number, number];
  return `${MONTHS[m - 1]} ${y}`;
}

/**
 * The month being held today, `YYYY-MM` — what a Strat chip opens on (owner,
 * 2026-10-27). The newest month is usually next month's preview, which is a
 * forecast, not a position.
 */
export function heldMonth(today: Date = new Date()): string {
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * How the status badge is coloured: settled, a live month-end run, or still
 * forming. A stage (near-close / close / confirm) is live only on a month not
 * yet begun; on a month already held it is the run that settled it, so it
 * reads calm like `final` (owner, 2026-10-27).
 */
export function statusTone(status: string, month?: string, held?: string): "final" | "live" | "preview" {
  if (status === "final") return "final";
  const stage = status === "near-close" || status === "close" || status === "confirm";
  if (stage && month && held && month.slice(0, 7) <= held) return "final";
  if (status === "near-close" || status === "close" || status === "confirm") return "live";
  return "preview";
}

/** `YYYY-MM` from a served date — a `Date` (Arrow) or an ISO string. */
function monthOf(v: unknown): string | null {
  if (v instanceof Date) return `${v.getUTCFullYear()}-${String(v.getUTCMonth() + 1).padStart(2, "0")}`;
  return typeof v === "string" && v.length >= 7 ? v.slice(0, 7) : null;
}

/**
 * Since as words: the month a holding began, or — for a fund being sold this
 * month — the span it was held, ending the month before `month`.
 */
function sinceText(since: unknown, weight: unknown, month: string | undefined): string | null {
  const start = monthOf(since);
  if (!start) return null;
  if (weight !== 0 || !month) return monthLabel(start);
  const end = shiftMonth(month, -1);
  const [sm, sy] = monthLabel(start).split(" ");
  const [em, ey] = monthLabel(end).split(" ");
  return `${sy === ey ? sm : `${sm} ${sy}`}–${em} ${ey} · sold`;
}

/**
 * The grid's copy of a Strat screen's frames with text columns added:
 * `stratEntry` — the entry close to two places, or the server's
 * `stratEntryNote` ("stand-in") where there is no close of the fund itself —
 * and `stratSinceText` (`sinceText`). `month` is the stepper's. Only `detail`
 * is touched.
 */
export function withEntryText(frames: readonly ScreenerFrame[], month?: string): ScreenerFrame[] {
  return frames.map((frame) => {
    if (frame.name !== "detail") return frame;
    const rows = frame.rows.map((row) => {
      const note = row.stratEntryNote;
      const close = row.stratEntryClose;
      const text = typeof note === "string" && note ? note : typeof close === "number" ? close.toFixed(2) : null;
      return { ...row, stratEntry: text, stratSinceText: sinceText(row.stratSince, row.stratWeight, month) };
    });
    const extra = ["stratEntry", "stratSinceText"].filter((n) => !frame.fields.some((f) => f.name === n));
    const fields = extra.length ? [...frame.fields, ...extra.map((name) => ({ name, type: "text" as const }))] : frame.fields;
    return { ...frame, rows, fields };
  });
}
